/**
 * AuditDashboard
 * 
 * Dashboard de Auditoria de Estoque
 * Exibe métricas e lista de auditorias com filtros
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ShieldCheck, Search, Filter, Plus, 
  Calendar, CheckCircle2, AlertTriangle, 
  Clock, ArrowRight, FileText, BarChart3,
  X, Loader2
} from 'lucide-react'
import { stockAuditService, brandService } from '../../../../services/erpApi'
import './AuditDashboard.css'

interface Audit {
  id: number
  audit_number: string
  brand_id: number
  brand_name: string
  status: 'pending' | 'in_progress' | 'completed' | 'applied' | 'cancelled'
  created_at: string
  created_by_name?: string
  responsible_user_name?: string
  total_items?: number
  divergent_items?: number
  items_with_discrepancy?: number
}

function AuditDashboard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [audits, setAudits] = useState<Audit[]>([])
  const [brands, setBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  const loadAudits = useCallback(async () => {
    try {
      setLoading(true)
      const response = await stockAuditService.getList({ limit: 1000 })
      if (response.success && response.data) {
        // Mapear campos do backend para o frontend
        const mappedAudits = Array.isArray(response.data) ? response.data.map((audit: any) => ({
          ...audit,
          audit_number: audit.code || audit.audit_number,
          brand_name: audit.brand_name_full || audit.brand_name,
          divergent_items: audit.items_with_discrepancy || audit.divergent_items || 0
        })) : []
        setAudits(mappedAudits)
      }
    } catch (error) {
      console.error('Erro ao carregar auditorias:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadBrands = useCallback(async () => {
    try {
      const response = await brandService.getAll()
      if (response.success && response.data) {
        setBrands(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error)
    }
  }, [])

  useEffect(() => {
    loadAudits()
    loadBrands()
  }, [loadAudits, loadBrands])

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const handleCreateAudit = async () => {
    if (!selectedBrand) {
      showToast('Por favor, selecione uma marca', 'error')
      return
    }

    setIsCreating(true)
    try {
      const brand = brands.find((b: any) => b.id === selectedBrand)
      const response = await stockAuditService.create({
        brandId: selectedBrand,
        brandName: brand?.name || `Marca ID ${selectedBrand}`
      })

      if (response.success) {
        showToast('Auditoria criada com sucesso!', 'success')
        setIsCreateModalOpen(false)
        setSelectedBrand(null)
        loadAudits()
        navigate(`/erp/stock/audits/${response.data.id}`)
      } else {
        showToast(response.message || 'Erro ao criar auditoria', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao criar auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao criar auditoria', 'error')
    } finally {
      setIsCreating(false)
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: 'Agendada', color: 'status-scheduled', icon: Calendar }
      case 'in_progress':
        return { label: 'Em Andamento', color: 'status-in-progress', icon: Clock }
      case 'completed':
        return { label: 'Concluída', color: 'status-completed', icon: CheckCircle2 }
      case 'applied':
        return { label: 'Aplicada', color: 'status-completed', icon: CheckCircle2 }
      case 'cancelled':
        return { label: 'Cancelada', color: 'status-divergent', icon: AlertTriangle }
      default:
        return { label: 'Desconhecido', color: 'status-default', icon: FileText }
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoje'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem'
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }

  const calculateAverageAccuracy = () => {
    const completedAudits = audits.filter(a => a.status === 'completed' || a.status === 'applied')
    if (completedAudits.length === 0) return null
    
    let totalAccuracy = 0
    let count = 0
    
    completedAudits.forEach(audit => {
      if (audit.total_items && audit.total_items > 0) {
        const divergent = audit.divergent_items || audit.items_with_discrepancy || 0
        const accuracy = ((audit.total_items - divergent) / audit.total_items) * 100
        totalAccuracy += accuracy
        count++
      }
    })
    
    return count > 0 ? (totalAccuracy / count).toFixed(1) : null
  }

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = !searchTerm || (
      audit.audit_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.brand_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const stats = {
    total: audits.length,
    inProgress: audits.filter(a => a.status === 'in_progress').length,
    divergent: audits.filter(a => {
      const divergentCount = a.divergent_items || a.items_with_discrepancy || 0
      return divergentCount > 0
    }).length,
    averageAccuracy: calculateAverageAccuracy()
  }

  return (
    <div className="audit-dashboard">
      {/* Header */}
      <div className="audit-dashboard-header">
        <div>
          <h1 className="audit-dashboard-title">
            Gestão de <span className="audit-dashboard-title-accent">Auditorias</span>
          </h1>
          <p className="audit-dashboard-subtitle">
            Controle de Inventário e Acuracidade
          </p>
        </div>
        <div className="audit-dashboard-actions">
          <button 
            className="audit-dashboard-btn-filter"
            onClick={() => {/* TODO: Implementar filtros avançados */}}
          >
            <Filter size={16} /> Filtros
          </button>
          <button 
            className="audit-dashboard-btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus size={16} /> Nova Auditoria
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="audit-dashboard-stats-grid">
        <div className="audit-dashboard-stat-card">
          <div className="audit-dashboard-stat-icon audit-dashboard-stat-icon-primary">
            <ShieldCheck size={20} />
          </div>
          <div className="audit-dashboard-stat-content">
            <p className="audit-dashboard-stat-label">Total de Auditorias</p>
            <p className="audit-dashboard-stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="audit-dashboard-stat-card">
          <div className="audit-dashboard-stat-icon audit-dashboard-stat-icon-blue">
            <Clock size={20} />
          </div>
          <div className="audit-dashboard-stat-content">
            <p className="audit-dashboard-stat-label">Em Andamento</p>
            <p className="audit-dashboard-stat-value">{stats.inProgress}</p>
          </div>
        </div>
        <div className="audit-dashboard-stat-card">
          <div className="audit-dashboard-stat-icon audit-dashboard-stat-icon-rose">
            <AlertTriangle size={20} />
          </div>
          <div className="audit-dashboard-stat-content">
            <p className="audit-dashboard-stat-label">Divergências</p>
            <p className="audit-dashboard-stat-value">{stats.divergent}</p>
          </div>
        </div>
        <div className="audit-dashboard-stat-card">
          <div className="audit-dashboard-stat-icon audit-dashboard-stat-icon-emerald">
            <BarChart3 size={20} />
          </div>
          <div className="audit-dashboard-stat-content">
            <p className="audit-dashboard-stat-label">Acuracidade Média</p>
            <p className="audit-dashboard-stat-value">
              {stats.averageAccuracy ? `${stats.averageAccuracy}%` : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="audit-dashboard-main-card">
        {/* Active Filters & Search */}
        <div className="audit-dashboard-filters-section">
          <div className="audit-dashboard-filters-row">
            <div className="audit-dashboard-search-wrapper">
              <Search className="audit-dashboard-search-icon" size={16} />
              <input 
                type="text" 
                placeholder="Buscar por ID ou título..." 
                className="audit-dashboard-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="audit-dashboard-status-filters">
              <span className="audit-dashboard-status-label">Status:</span>
              {['all', 'pending', 'in_progress', 'completed', 'applied', 'cancelled'].map((s) => {
                const config = getStatusConfig(s)
                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`audit-dashboard-status-filter-btn ${
                      statusFilter === s ? 'audit-dashboard-status-filter-btn-active' : ''
                    }`}
                  >
                    {s === 'all' ? 'Todos' : config.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="audit-dashboard-loading">
            <Loader2 size={24} className="audit-dashboard-spinner" />
            <p>Carregando auditorias...</p>
          </div>
        ) : (
          <div className="audit-dashboard-table-wrapper">
            <table className="audit-dashboard-table">
              <thead>
                <tr>
                  <th>Auditoria</th>
                  <th>Marca / Itens</th>
                  <th>Responsável / Data</th>
                  <th className="text-center">Divergências</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAudits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="audit-dashboard-empty">
                      <p>Nenhuma auditoria encontrada</p>
                    </td>
                  </tr>
                ) : (
                  filteredAudits.map((audit) => {
                    const statusConfig = getStatusConfig(audit.status)
                    const StatusIcon = statusConfig.icon
                    const divergentCount = audit.divergent_items || audit.items_with_discrepancy || 0
                    const hasDivergence = divergentCount > 0

                    return (
                      <tr 
                        key={audit.id} 
                        className="audit-dashboard-table-row"
                        onClick={() => {
                          if (audit.status === 'completed' || audit.status === 'applied') {
                            navigate(`/erp/stock/audits/${audit.id}/manage`)
                          } else {
                            navigate(`/erp/stock/audits/${audit.id}`)
                          }
                        }}
                      >
                        <td>
                          <div className="audit-dashboard-audit-info">
                            <p className="audit-dashboard-audit-title">
                              {audit.brand_name || 'Auditoria Geral'}
                            </p>
                            <span className="audit-dashboard-audit-id">{audit.audit_number}</span>
                          </div>
                        </td>
                        <td>
                          <div className="audit-dashboard-audit-details">
                            <span className="audit-dashboard-audit-brand">
                              {audit.brand_name || `Marca ID ${audit.brand_id}`}
                            </span>
                            <span className="audit-dashboard-audit-items">
                              {audit.total_items || 0} itens
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="audit-dashboard-audit-details">
                            <span className="audit-dashboard-audit-responsible">
                              {audit.responsible_user_name || audit.created_by_name || 'N/A'}
                            </span>
                            <span className="audit-dashboard-audit-date">
                              {formatDate(audit.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="text-center">
                          {hasDivergence ? (
                            <span className="audit-dashboard-divergence-badge">
                              {divergentCount}
                            </span>
                          ) : (
                            <span className="audit-dashboard-no-divergence">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`audit-dashboard-status-badge ${statusConfig.color}`}>
                            <StatusIcon size={12} />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="text-right">
                          <button 
                            className="audit-dashboard-action-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (audit.status === 'completed' || audit.status === 'applied') {
                                navigate(`/erp/stock/audits/${audit.id}/manage`)
                              } else {
                                navigate(`/erp/stock/audits/${audit.id}`)
                              }
                            }}
                          >
                            <ArrowRight size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Audit Modal */}
      {isCreateModalOpen && (
        <div 
          className="audit-dashboard-modal-overlay" 
          onClick={() => !isCreating && setIsCreateModalOpen(false)}
        >
          <div 
            className="audit-dashboard-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="audit-dashboard-modal-content">
              <div className="audit-dashboard-modal-header">
                <div className="audit-dashboard-modal-header-left">
                  <div className="audit-dashboard-modal-icon">
                    <FileText size={24} />
                  </div>
                  <div>
                    <h2 className="audit-dashboard-modal-title">Nova Auditoria</h2>
                    <p className="audit-dashboard-modal-subtitle">Criar auditoria manual por marca</p>
                  </div>
                </div>
                {!isCreating && (
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="audit-dashboard-modal-close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="audit-dashboard-modal-body">
                <div className="audit-dashboard-form-group">
                  <label className="audit-dashboard-form-label">
                    Selecione a Marca
                  </label>
                  <select
                    className="audit-dashboard-form-select"
                    value={selectedBrand || ''}
                    onChange={(e) => setSelectedBrand(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={isCreating}
                  >
                    <option value="">Selecione uma marca...</option>
                    {brands.map((brand: any) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                  <p className="audit-dashboard-form-help">
                    Todos os produtos com estoque desta marca serão incluídos na auditoria
                  </p>
                </div>

                <div className="audit-dashboard-modal-actions">
                  <button
                    className="audit-dashboard-btn-secondary"
                    onClick={() => setIsCreateModalOpen(false)}
                    disabled={isCreating}
                  >
                    Cancelar
                  </button>
                  <button
                    className="audit-dashboard-btn-primary"
                    onClick={handleCreateAudit}
                    disabled={isCreating || !selectedBrand}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 size={16} className="audit-dashboard-spinner" />
                        Criando...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Criar Auditoria
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className={`audit-dashboard-toast audit-dashboard-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}

export default AuditDashboard
